// ============================================================
// TNT XPDD Road Express - 运费计算器
// ============================================================
// 文件依赖:
//   /data/rates.json       - 费率表 (92 Zones)
//   /data/zones.json       - 邮编映射 (3,346 postcodes → Zone + State)
//   /data/remote.json      - 偏远地区邮编列表 (1,322 postcodes)
//   /data/surcharges.json  - 附加费配置
//
// 燃油附加费率 fuelSurchargeRate 默认 38.1% (0.381)，需定期更新
//
// 注意: 偏远地区判定使用近似匹配 (Excel VLOOKUP 省略第4参数 = 默认 TRUE)
//       代码中使用排序后的二分查找实现近似匹配
// ============================================================

const ratesData = require('./data/rates.json');
const zonesData = require('./data/zones.json');
const remoteData = require('./data/remote.json');
const surchargesData = require('./data/surcharges.json');

const rates = ratesData;
const zones = zonesData;
// Remote postcodes sorted for approximate match (matching Excel VLOOKUP without 4th param)
const remotePostcodesSorted = [...remoteData].map(Number).sort((a, b) => a - b);
const surcharges = surchargesData;

const DEFAULT_FUEL_RATE = 0.381; // 38.1%

// ---- 辅助函数 ----

function lookupZone(pc) {
  return zones[pc] || null;
}

function lookupRate(zone) {
  return rates[zone] || null;
}

/**
 * 偏远地区判定 — 近似匹配 (Approximate Match)
 * 对应 Excel: IFERROR(VLOOKUP(postcode, Remote!A:B, 2), 0)
 * Excel VLOOKUP 省略第4参数时默认为 TRUE (近似匹配)
 * 在已排序列表中找到 ≤ 搜索值的最大值，如果找到则收取偏远附加费
 */
function isRemoteArea(pc) {
  const pcNum = parseInt(pc, 10);
  if (isNaN(pcNum)) return false;
  
  // Binary search for the largest value <= pcNum
  let lo = 0, hi = remotePostcodesSorted.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (remotePostcodesSorted[mid] <= pcNum) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  
  // If we found a value <= pcNum, approximate match succeeds
  return found >= 0;
}

/**
 * 体积重量 = L(cm) × W(cm) × H(cm) / 4000
 */
function calcCubicWeight(l, w, h) {
  return (l * w * h) / 4000;
}

/**
 * MHP (Manual Handling Processing) 判断
 * 对应 Excel: IF(OR(MAX(dims)<200, LARGE(dims,2)<100, MIN(dims)<15,
 *                   SQRT(SUMSQ(dims))>120, weight<0.25), 16.5, 0)
 *   MAX(dims) < 200  → 最长边 < 200cm
 *   LARGE(dims,2) < 100  → 第二长边 < 100cm
 *   MIN(dims) < 15  → 最短边 < 15cm
 *   SQRT(SUMSQ) > 120  → 对角线 > 120cm
 *   weight < 0.25  → 重量 < 250g
 */
function shouldChargeMHP(pkg) {
  const dims = [pkg.lengthCm, pkg.widthCm, pkg.heightCm].sort((a, b) => b - a);
  const maxDim = dims[0];
  const midDim = dims[1];
  const minDim = dims[2];
  const diagonal = Math.sqrt(
    pkg.lengthCm ** 2 + pkg.widthCm ** 2 + pkg.heightCm ** 2
  );

  return (
    maxDim < 200 ||     // 最长边 < 200cm
    midDim < 100 ||     // 第二长边 < 100cm
    minDim < 15 ||      // 最短边 < 15cm
    diagonal > 120 ||   // 对角线 > 120cm
    pkg.weightKg < 0.25 // 重量 < 250g
  );
}

/**
 * WA 附加费: State = ""WA"" 时加收 10% (含 Perth)
 * 对应 Excel: IF(E=""WA"", ...*1.1, ...)
 */
function isWAState(state) {
  return state.trim() === 'WA';
}

/**
 * 超尺寸附加费 (按最大单边 metres)
 */
function calcOversizeFee(pkg) {
  const maxM = Math.max(pkg.lengthCm, pkg.widthCm, pkg.heightCm) / 100;
  for (const t of surcharges.oversize) {
    if (maxM >= t.minMetres && maxM <= (t.maxMetres ?? Infinity)) return t.fee;
  }
  return 0;
}

/**
 * Tail Lift 附加费
 */
function calcTailLiftFee(wt, tiers) {
  for (const t of tiers) {
    if (wt >= t.minKg && wt <= (t.maxKg ?? Infinity)) return t.fee;
  }
  return 0;
}

// ============================================================
// 主计算函数
// ============================================================
/**
 * 计算 TNT 运费
 *
 * 对应 Excel 公式 (数据表!I列):
 *   =IF(AND(MAX(dims)<120, weight<=30),
 *     IF(state=""WA"",
 *       MAX(basic+CEIL(cw)*kiloRate, min) * (1+fuelRate) * 1.1,
 *       MAX(basic+CEIL(cw)*kiloRate, min) * (1+fuelRate)
 *     )
 *     + IFERROR(VLOOKUP(postcode, Remote!A:B, 2, FALSE), 0)
 *     + IF(MHP条件, 16.5, 0)
 *   , """")
 *
 * @param {string} postcode - 收件邮编
 * @param {number} weight - 实际重量 (kg)
 * @param {number} length - 长 (cm)
 * @param {number} width - 宽 (cm)，默认 30
 * @param {number} height - 高 (cm)，默认 30
 * @param {object} options - 可选配置
 * @param {boolean} options.isResidentialPickup - 住宅取件
 * @param {boolean} options.isResidentialDelivery - 住宅派送
 * @param {boolean} options.isDangerousGoods - 危险品
 * @param {boolean} options.needsTailLiftPickup - 需要升降尾板取件
 * @param {boolean} options.needsTailLiftDelivery - 需要升降尾板派送
 * @param {number} options.fuelSurchargeRate - 燃油附加费率，默认 0.381
 * @returns {number} 运费金额 (Ex GST，与 Excel I列一致) 或错误时返回 99999
 */
function calculateShipping(postcode, weight = 0, length = 0, width = 30, height = 30, options = {}) {
  const errors = [];
  const warnings = [];
  const fuelRate = options.fuelSurchargeRate ?? DEFAULT_FUEL_RATE;

  const pkg = {
    lengthCm: length,
    widthCm: width,
    heightCm: height,
    weightKg: weight
  };

  const result = {
    success: false,
    zone: '',
    state: '',
    chargeableWeightKg: 0,
    cubicWeightKg: 0,
    baseFreight: 0,
    fuelSurcharge: 0,
    waRegionalSurcharge: 0,
    remoteSurcharge: 0,
    mhpFee: 0,
    residentialPickupFee: 0,
    residentialDeliveryFee: 0,
    heavyResidentialPickupFee: 0,
    heavyResidentialDeliveryFee: 0,
    dangerousGoodsFee: 0,
    oversizeFee: 0,
    tailLiftPickupFee: 0,
    tailLiftDeliveryFee: 0,
    totalExGST: 0,
    gst: 0,
    totalIncGST: 0,
    errors,
    warnings,
  };

  // 1. Zone 查找
  const zi = lookupZone(postcode);
  if (!zi) {
    errors.push('邮编 ' + postcode + ' 未找到对应 Zone');
    return 99999;
  }
  result.zone = zi.zone;
  result.state = zi.state;

  // 2. 费率查找
  const rate = lookupRate(zi.zone);
  if (!rate) {
    errors.push('Zone ' + zi.zone + ' 费率未找到');
    return 99999;
  }

  // 3. 计费重量 = MAX(体积重, 实重)
  const cubic = calcCubicWeight(pkg.lengthCm, pkg.widthCm, pkg.heightCm);
  const cw = Math.max(cubic, pkg.weightKg);
  result.cubicWeightKg = cubic;
  result.chargeableWeightKg = cw;

  // 4. 校验: maxDim < 120cm, chargeableWeight ≤ 30kg
  const maxDim = Math.max(pkg.lengthCm, pkg.widthCm, pkg.heightCm);
  if (maxDim >= 120) {
    errors.push('最大单边 ' + maxDim + 'cm >= 120cm，超出 XPDD 限制');
    return 99999;
  }
  if (cw > 30) {
    errors.push('计费重量 ' + cw.toFixed(2) + 'kg > 30kg，超出 XPDD 限制');
    return 99999;
  }

  // 5. 基础运费 = MAX(Basic + CEIL(cw) × KiloRate, Minimum)
  //    对应 Excel: IF(F+(CEILING(weight,1)*G)>H, F+(CEILING(weight,1)*G), H)
  const baseCalc = rate.basic + Math.ceil(cw) * rate.kiloRate;
  result.baseFreight = Math.max(baseCalc, rate.min);

  // 6. 燃油附加费
  //    对应 Excel: baseFreight * (1 + [输出表格.xlsx]结果表!$R$2)
  result.fuelSurcharge = result.baseFreight * fuelRate;
  let sub = result.baseFreight + result.fuelSurcharge;

  // 7. WA 10% 附加费 (所有 WA 邮编，含 Perth)
  //    对应 Excel: IF(E=""WA"", sub * 1.1, sub)
  if (isWAState(zi.state)) {
    result.waRegionalSurcharge = sub * surcharges.waRegionalSurchargeRate;
    sub += result.waRegionalSurcharge;
  }

  // 8. 偏远地区 $37 — 精确匹配
  //    对应 Excel: IFERROR(VLOOKUP(A, Remote!A:B, 2, FALSE), 0)
  //    注意: VLOOKUP 必须使用 FALSE (精确匹配)，否则近似匹配会导致
  //          不在 Remote 列表中的邮编也被错误收取 $37 附加费
  if (isRemoteArea(postcode)) {
    result.remoteSurcharge = surcharges.remoteFee;
  }

  // 9. MHP $16.5
  //    对应 Excel: IF(OR(MAX<200, LARGE(,2)<100, MIN<15, SQRT(SUMSQ)>120, weight<0.25), 16.5, 0)
  if (shouldChargeMHP(pkg)) {
    result.mhpFee = surcharges.mhpFee;
  }

  // 10. 危险品
  if (options.isDangerousGoods) {
    result.mhpFee = surcharges.mhpFee;
    result.dangerousGoodsFee = surcharges.dangerousGoods;
  }

  // 11. 住宅附加费
  if (options.isResidentialPickup) {
    result.residentialPickupFee = surcharges.residentialPickup;
    if (cw >= 100) result.heavyResidentialPickupFee = surcharges.heavyResPickup100plus;
    else if (cw >= 30) result.heavyResidentialPickupFee = surcharges.heavyResPickup30to99;
  }
  if (options.isResidentialDelivery) {
    result.residentialDeliveryFee = surcharges.residentialDelivery;
    if (cw >= 100) result.heavyResidentialDeliveryFee = surcharges.heavyResDelivery100plus;
    else if (cw >= 30) result.heavyResidentialDeliveryFee = surcharges.heavyResDelivery30to99;
  }

  // 12. 超尺寸 + Tail Lift
  result.oversizeFee = calcOversizeFee(pkg);
  if (options.needsTailLiftPickup)
    result.tailLiftPickupFee = calcTailLiftFee(cw, surcharges.tailLiftPickup);
  if (options.needsTailLiftDelivery)
    result.tailLiftDeliveryFee = calcTailLiftFee(cw, surcharges.tailLiftDelivery);

  // 13. 汇总
  result.totalExGST = sub
    + result.remoteSurcharge + result.mhpFee
    + result.residentialPickupFee + result.residentialDeliveryFee
    + result.heavyResidentialPickupFee + result.heavyResidentialDeliveryFee
    + result.dangerousGoodsFee + result.oversizeFee
    + result.tailLiftPickupFee + result.tailLiftDeliveryFee;

  result.gst = result.totalExGST * 0.1;
  result.totalIncGST = result.totalExGST + result.gst;
  result.success = true;

  // 返回不含 GST 的总价（与 Excel 数据表!I列公式结果一致）
  return result.totalExGST;
}

module.exports = { calculateShipping };

// ============================================================
// 使用示例
// ============================================================
// const { calculateShipping } = require('./tnt');
//
// const fee = calculateShipping('2000', 5, 40, 30, 20);
// console.log(fee); // → 不含 GST 运费（与 Excel 数据表!I列一致）
//
// // 带选项:
// const fee2 = calculateShipping('3000', 10, 50, 40, 30, {
//   isResidentialDelivery: true,
//   fuelSurchargeRate: 0.40
// });
